/**
 * Framework7 per-route `beforeEnter` guards.
 *
 * ============================================================================
 *  THESE GUARDS ARE UX, NOT SECURITY.
 * ============================================================================
 *
 * They read the Vuex `current_user` module. `vuex-persist` writes that module
 * straight to `localStorage` under the key `private-gym` (see
 * `src/store/index.js:10-13`), so the JSON these guards trust is trivially
 * editable by anyone with devtools open. Flipping `is_super_admin` to `true`
 * in localStorage will walk past every guard in this file.
 *
 * The ONLY real authorization control lives in the API: the `admin` and
 * `super_admin` middleware aliases in `api.privategym` reject the request with
 * a 403 no matter what the client believes about itself. These guards exist so
 * members are not shown admin screens whose every data call would 403 — that
 * is all they are for.
 *
 * `tests/components/auth/RouteGuards.test.js` pins this on purpose, in
 * `guard_is_ux_only_a_forged_store_still_resolves`, so the assumption is
 * written down as a passing test rather than left implied.
 *
 * Callback contract: Framework7 8.3.4 calls each queue item with ONE
 * destructured object — `{ router, to, from, resolve, reject, direction, app }`
 * (`node_modules/framework7/modules/router/process-route-queue.js:20-32`).
 * This is NOT the positional four-argument signature shown in the Framework7
 * v4/v5 docs. The already-wired global hook at `src/components/app.vue:226-235`
 * uses the same destructured shape.
 */

import { store } from '../store/index';

/**
 * Undefined-safe read of the logged-in user's attributes.
 *
 * Before login `current_user.data` holds only `{ is_admin: 0, ... }`, and after
 * `clear_all` it can be replaced wholesale, so every access here is defensive.
 * Returning `{}` rather than `undefined` means predicates never have to
 * optional-chain and can never throw on a missing key (T-36-28).
 *
 * @returns {Object} the current user's attributes, or an empty object
 */
function currentUserData() {
  if (!store || !store.state || !store.state.current_user) {
    return {};
  }
  return store.state.current_user.data || {};
}

/**
 * Builds a Framework7 `beforeEnter` guard from a predicate.
 *
 * The returned function takes a SINGLE destructured object, matching the
 * Framework7 8.3.4 contract. `resolve`, `reject` and `router` are each checked
 * with `typeof` before being called so that:
 *
 *  1. the guard can be invoked directly from a unit test with only the pieces
 *     that case cares about, and
 *  2. a future copy-paste of the positional v4/v5 signature fails CLOSED —
 *     none of the callbacks are found, so navigation simply never resolves,
 *     rather than throwing an opaque TypeError deep inside the router queue.
 *
 * @param {function(Object): boolean} predicate  receives the current user's attributes
 * @param {string} [fallback='/']                route to send a rejected user to
 * @returns {function(Object): void}             a Framework7 beforeEnter guard
 */
export function guard(predicate, fallback = '/') {
  return function beforeEnter(context) {
    const { resolve, reject, router } = context || {};

    if (predicate(currentUserData())) {
      if (typeof resolve === 'function') {
        resolve();
      }
      return;
    }

    if (typeof reject === 'function') {
      reject();
    }
    if (router && typeof router.navigate === 'function') {
      router.navigate(fallback);
    }
  };
}

/**
 * Allows the existing plain-admin screens.
 *
 * TRUTHY on purpose: `CurrentUserResource` sends `is_admin` as the raw integer
 * `0`/`1`, not a boolean (D-03 — `src/pages/users/_user_id/UserId.vue:363`
 * depends on it staying an int). A strict `=== true` here would lock out all
 * 13 existing admins.
 */
export const requireAdmin = guard((user) => Boolean(user.is_admin));

/**
 * Reserved for the super-admin-only screens arriving in Phases 43 (news) and
 * 44 (broadcast). Deliberately exported and unit-tested but not yet attached
 * to any route — the screens do not exist yet.
 *
 * STRICT on purpose: `is_super_admin` is a real JSON boolean (Plan 05), so
 * `=== true` is safe and rejects a truthy `1` that could only come from a
 * hand-edited store or a contract regression.
 */
export const requireSuperAdmin = guard((user) => user.is_super_admin === true);
